import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Clock,
  Square,
  Users,
  LogOut,
  Umbrella,
  X,
  MessageCircle,
  MoreVertical,
  User as UserIcon
} from 'lucide-react';
import { format } from 'date-fns';
import Avatar from './Avatar';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { useIdleDetection, getIdlePeriodsFromStorage, clearIdlePeriodsFromStorage } from './IdleDetection';
import { ActivityMonitor } from './IdleDetection';
import QuickAccess from './QuickAccess';
import Sidebar from './Sidebar';
import ThemeToggle from './ThemeToggle';
import AnnouncementBanner from './AnnouncementBanner';
import SpotifyPlayer from './spotify/SpotifyPlayer';
import NotificationBell from './notifications/NotificationBell';
import { getPageTitle, ROUTES } from '../constants/routes';
import { useUnreadCount, useIncomingChatRequests, useSystemSettings } from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';

const Layout = ({ children }) => {
  const { activeTimer, stopTimer, elapsedTime } = useTimer();
  const [idlePeriods, setIdlePeriods] = useState([]);
  const { user, logout } = useAuth();
  const { data: chatUnread } = useUnreadCount();
  const { data: incomingChatRequests = [] } = useIncomingChatRequests();
  const pendingRequestCount = (incomingChatRequests || []).filter(
    (r) => r.status === 'pending' || !r.status
  ).length;
  const chatBadgeCount = (chatUnread?.unread_count || 0) + pendingRequestCount;
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuRef = useRef(null);
  const navigate = useNavigate();
  const [currentLeave, setCurrentLeave] = useState(null);
  const [showTimerStopModal, setShowTimerStopModal] = useState(false);
  const [timerStopDescription, setTimerStopDescription] = useState('');
  const [timerStopLoading, setTimerStopLoading] = useState(false);
  const location = useLocation();
  const isHelpPage = location.pathname === ROUTES.HELP;
  const isMessagesPage = location.pathname === ROUTES.MESSAGES;
  const isFullBleedPage = isHelpPage || isMessagesPage;
  const { data: systemSettings } = useSystemSettings();
  const dailyHoursCap = systemSettings?.daily_hours_cap ?? SYSTEM_SETTINGS_DEFAULTS.daily_hours_cap;
  const minDescLen = systemSettings?.min_description_length ?? SYSTEM_SETTINGS_DEFAULTS.min_description_length;
  const idleThresholdMinutes = systemSettings?.idle_threshold_minutes ?? SYSTEM_SETTINGS_DEFAULTS.idle_threshold_minutes;
  const idleDetectionEnabled = !!activeTimer;
  const idleDetection = useIdleDetection(idleDetectionEnabled ? idleThresholdMinutes * 60 * 1000 : null);
  const isIdle = idleDetection.isIdle;
  const lastTimerIdRef = useRef(null);

  useEffect(() => {
    if (activeTimer?.id) {
      lastTimerIdRef.current = activeTimer.id;
    }
  }, [activeTimer]);

  // Mobile sidebar: body scroll lock while open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [showMobileMenu]);

  // Mobile sidebar / menus: Escape to close + close when crossing lg breakpoint
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showMobileMenu) setShowMobileMenu(false);
        if (showHeaderMenu) setShowHeaderMenu(false);
      }
    };
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setShowMobileMenu(false);
        setShowHeaderMenu(false);
      }
    };
    const handleClickOutside = (e) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) {
        setShowHeaderMenu(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMenu, showHeaderMenu]);

  React.useEffect(() => {
    checkCurrentLeave();
  }, [user]);

  const checkCurrentLeave = async () => {
    if (!user || (user.role !== 'member' && user.role !== 'project_lead')) return;
    
    try {
      const response = await axios.get('/api/leaves/my-leaves');
      const now = new Date();
      const activeLeave = response.data.find(leave => {
        const startDate = new Date(leave.start_date);
        const endDate = new Date(leave.end_date);
        return leave.status === 'approved' && startDate <= now && endDate >= now; // Current approved leave
      }) || response.data.find(leave => {
        const startDate = new Date(leave.start_date);
        return leave.status === 'approved' && startDate > now; // Upcoming approved leave
      });
      
      setCurrentLeave(activeLeave || null);
    } catch (error) {
      console.error('Failed to check leave status:', error);
      setCurrentLeave(null);
    }
  };

  const handleStopTimer = () => {
    setShowTimerStopModal(true);
  };

  // Function to check daily hours limit for timer stop
  const checkDailyHoursLimitForTimer = async () => {
    try {
      // Use backend daily-hours endpoint for consistent timezone handling
      const response = await axios.get('/api/time-entries/daily-hours');
      const totalHoursToday = response.data.total_hours;
      
      // Calculate current timer duration
      if (!activeTimer) {
        return true; // No active timer, allow
      }
      
      const timerStartTime = new Date(activeTimer.start_time);
      const now = new Date();
      const timerDurationMs = now - timerStartTime;
      const currentTimerHours = timerDurationMs / (1000 * 60 * 60); // Convert to hours
      
      const totalAfterTimer = totalHoursToday + currentTimerHours;
      
      if (totalAfterTimer > dailyHoursCap) {
        const remainingHours = Math.max(0, dailyHoursCap - totalHoursToday);
        toast.warning(
          `Timer will be capped at ${remainingHours.toFixed(1)}h (saved) to stay within the daily limit of ${dailyHoursCap}h. You have tracked ${totalHoursToday.toFixed(1)} hours today.`
        );
      }
      
      return true;
    } catch (error) {
      console.error('Error checking daily hours for timer:', error);
      // If there's an error checking, allow the timer stop (backend will handle validation)
      return true;
    }
  };

  const handleConfirmStopTimer = async () => {
    if (!timerStopDescription.trim()) {
      toast.error('Please provide a description for the timer session');
      return;
    }

    if (timerStopDescription.trim().length < minDescLen) {
      toast.error(`Description must be at least ${minDescLen} characters long`);
      return;
    }

    // Check daily hours limit before stopping timer
    const canStopTimer = await checkDailyHoursLimitForTimer();
    if (!canStopTimer) {
      return;
    }

    setTimerStopLoading(true);
    try {
      await stopTimer(timerStopDescription.trim());
      setShowTimerStopModal(false);
      setTimerStopDescription('');
    } catch (error) {
      console.error('Error stopping timer:', error);
    } finally {
      setTimerStopLoading(false);
    }
  };

  // Utility function to format time
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-screen h-[100dvh] bg-secondary-50 dark:bg-secondary-900 flex overflow-hidden">
      {/* Mobile menu overlay */}
      {showMobileMenu && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowMobileMenu(false)}
          aria-hidden="true"
        />
      )}
      
      {/* Sidebar - Fixed on mobile, static on desktop */}
      <div 
        key="main-sidebar"
        id="main-sidebar"
        className={`fixed inset-y-0 left-0 z-50 w-56 isolate shadow-lg lg:static lg:shadow-none transform transition-transform duration-300 ease-in-out ${
          showMobileMenu ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        style={{ willChange: 'transform' }}
      >
        <Sidebar onClose={() => setShowMobileMenu(false)} />
      </div>

      {/* Main content area with fixed header and scrollable content */}
      <div
        className={`flex-1 flex flex-col min-w-0 ${
          isFullBleedPage ? 'bg-white dark:bg-secondary-900' : ''
        }`}
      >
        {/* Fixed Header */}
        <header className="fixed top-0 right-0 left-0 lg:left-56 z-40 bg-white dark:bg-secondary-900 border-b border-secondary-200 dark:border-secondary-700 flex items-center h-16 lg:h-20 px-4 lg:px-6">
          {/* Mobile menu button */}
          <button
            onClick={() => setShowMobileMenu(true)}
            className="lg:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-600 hover:text-primary-600 mr-2 sm:mr-3"
            aria-label="Open navigation menu"
            aria-expanded={showMobileMenu}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <h1 className="text-lg lg:text-xl font-semibold text-secondary-900 dark:text-white truncate">
            {getPageTitle(location.pathname)}
          </h1>
          
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 ml-auto min-w-0">
            {/* Active Timer Indicator — compact on mobile */}
            {activeTimer && (
                <div 
                  className="flex items-center gap-1.5 sm:gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-2 sm:px-4 py-1.5 sm:py-2 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-all duration-200 shadow-sm hover:shadow-md min-w-0"
                title={`${activeTimer.project_name || 'Unknown Project'} - ${activeTimer.description || 'No description'}`}
                onClick={() => navigate('/time-entries')}
              >
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full animate-pulse shadow-sm"></div>
                  <span className="text-green-800 dark:text-green-200 font-mono text-xs sm:text-sm font-bold">
                    {formatTime(elapsedTime)}
                  </span>
                </div>
                
                <div className="hidden md:flex flex-col min-w-0">
                  <span className="text-xs text-green-700 dark:text-green-300 font-medium truncate max-w-32">
                    {activeTimer.task_name || activeTimer.description || 'Active Timer'}
                  </span>
                  {activeTimer.project_name && (
                    <span className="text-xs text-green-600 dark:text-green-400 truncate max-w-32">
                      {activeTimer.project_name}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStopTimer();
                    }}
                    className="text-green-700 dark:text-green-300 hover:text-rose-600 dark:hover:text-rose-400 p-1.5 min-h-[36px] min-w-[36px] flex items-center justify-center rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-all duration-200"
                    title="Stop Timer"
                    aria-label="Stop Timer"
                  >
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <span className="hidden sm:inline text-xs text-green-600 dark:text-green-400 font-semibold">STOP</span>
                </div>
              </div>
            )}
            
            {/* Idle Indicator */}
            {activeTimer && isIdle && (
              <span className="text-yellow-600 flex items-center" title="You are idle">
                <Clock className="h-4 w-4 lg:h-5 lg:w-5" />
                <span className="ml-1 text-xs font-semibold hidden md:inline">
                  Idle for {(() => {
                    const idleStartTime = idleDetection.idleStartTime;
                    const idleDuration = idleStartTime ? Date.now() - idleStartTime : 0;
                    const minutes = Math.floor(idleDuration / 60000);
                    const seconds = Math.floor((idleDuration % 60000) / 1000);
                    return `${minutes}m ${seconds}s`;
                  })()}
                </span>
              </span>
            )}
            
            {/* Leave Status — desktop only; shown in overflow on mobile */}
            {currentLeave && (
              <div className="hidden lg:flex items-center gap-2 px-3 py-1 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm font-medium max-w-[12rem] truncate">
                <Umbrella className="h-4 w-4 shrink-0" />
                <span className="truncate">On Leave - {currentLeave.description}</span>
              </div>
            )}

            {/* Spotify — compact control just before chat */}
            <SpotifyPlayer />

            {/* Chat */}
            <button
              onClick={() => navigate(ROUTES.MESSAGES)}
              className={`p-2 min-h-[44px] min-w-[44px] flex items-center justify-center relative ${
                isMessagesPage
                  ? 'text-primary-600'
                  : 'text-secondary-600 hover:text-primary-600 dark:text-secondary-300'
              }`}
              title="Messages"
              aria-label="Open messages"
            >
              <MessageCircle className="h-5 w-5" />
              {chatBadgeCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 lg:h-5 lg:w-5 flex items-center justify-center">
                  {chatBadgeCount > 99 ? '99+' : chatBadgeCount}
                </span>
              )}
            </button>

            <NotificationBell />

            {/* Desktop: theme, user, logout */}
            <div className="hidden sm:flex items-center gap-2 lg:gap-4">
              <ThemeToggle />
              <button
                onClick={() => navigate('/profile')}
                className="flex flex-col items-end group min-w-0"
                title="View Profile"
              >
                <span className="font-medium leading-tight group-hover:underline text-sm lg:text-base truncate max-w-[8rem] lg:max-w-[12rem]">
                  {user?.full_name || user?.username}
                </span>
                <span className="text-xs text-secondary-500 hidden md:block">
                  {user?.role === 'project_lead' ? 'Project Lead' : user?.role}
                </span>
              </button>
              <button
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                onClick={logout}
                title="Logout"
                aria-label="Logout"
              >
                <LogOut className="h-5 w-5 text-red-500" />
              </button>
            </div>

            {/* Mobile overflow menu: theme, profile, logout */}
            <div className="relative sm:hidden" ref={headerMenuRef}>
              <button
                type="button"
                onClick={() => setShowHeaderMenu((v) => !v)}
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-600 hover:text-primary-600"
                aria-label="More actions"
                aria-expanded={showHeaderMenu}
              >
                <MoreVertical className="h-5 w-5" />
              </button>
              {showHeaderMenu && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-lg z-50 py-1">
                  {currentLeave && (
                    <div className="px-3 py-2 text-xs text-green-700 dark:text-green-300 border-b border-secondary-100 dark:border-secondary-700 flex items-center gap-2">
                      <Umbrella className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">On Leave — {currentLeave.description}</span>
                    </div>
                  )}
                  <div className="px-2 py-1.5 flex items-center justify-between border-b border-secondary-100 dark:border-secondary-700">
                    <span className="text-sm text-secondary-600 dark:text-secondary-300 px-1">Theme</span>
                    <ThemeToggle />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowHeaderMenu(false);
                      navigate('/profile');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-3 text-sm text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-700 min-h-[44px]"
                  >
                    <UserIcon className="h-4 w-4" />
                    <span className="truncate">{user?.full_name || user?.username || 'Profile'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowHeaderMenu(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 min-h-[44px]"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Announcement / personal notification banners */}
        <div className="mt-16 lg:mt-20 flex-shrink-0 z-30 relative">
          <AnnouncementBanner />
        </div>

        {/* Scrollable Main Content */}
        <main
          className={`flex-1 overflow-y-auto ${
            isFullBleedPage
              ? 'bg-white dark:bg-secondary-900 p-0'
              : 'p-4 lg:p-8'
          }`}
        >
          {children}
        </main>
      </div>
      <ActivityMonitor
        activeTimer={activeTimer}
        setIdlePeriods={setIdlePeriods}
        idlePeriods={idlePeriods}
        onStopTimer={stopTimer}
      />
      {!isMessagesPage && <QuickAccess />}

      {/* Timer Stop Description Modal */}
      {showTimerStopModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowTimerStopModal(false)}>
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100 animate-in fade-in-0 zoom-in-95" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100 dark:border-secondary-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <Square className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Stop Timer</h3>
                  <p className="text-sm text-gray-500 dark:text-secondary-400">Add a description for your work session</p>
                </div>
              </div>
              <button
                onClick={() => setShowTimerStopModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-secondary-400 dark:hover:text-white dark:hover:bg-secondary-700 rounded-lg transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 pt-4">
              <div className="space-y-5">
                {/* Timer Duration Display */}
                <div className="bg-gray-50 dark:bg-secondary-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-secondary-300">Session Duration:</span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {activeTimer && formatTime(elapsedTime)}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                    Description <span className="text-red-500 text-xs font-normal">*</span>
                    <span className="text-xs text-gray-500 dark:text-secondary-400 font-normal ml-1">(min. {minDescLen} characters)</span>
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none transition-all duration-200"
                    placeholder="What did you work on during this session? Please provide detailed description of your work..."
                    rows={3}
                    value={timerStopDescription}
                    onChange={e => setTimerStopDescription(e.target.value)}
                    required
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className={`text-xs font-medium ${timerStopDescription.length < minDescLen ? 'text-red-500' : 'text-green-600'}`}>
                      {timerStopDescription.length}/{minDescLen}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-100 dark:border-secondary-700 mt-6">
                <button
                  type="button"
                  onClick={() => setShowTimerStopModal(false)}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 dark:text-secondary-300 bg-gray-100 dark:bg-secondary-700 hover:bg-gray-200 dark:hover:bg-secondary-600 rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  onClick={handleConfirmStopTimer}
                  className="flex-1 px-4 py-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={timerStopLoading || !timerStopDescription.trim() || timerStopDescription.trim().length < minDescLen}
                >
                  {timerStopLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Stopping...
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4" />
                      Stop Timer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
